/**
 * (C) 2025 Beyond Demise, All Rights Reserved
 *
 * Dashboard data loader (#239). Fetches the combined dashboard payload
 * from `GET /api/account` via `tdFetch` (bearer token + transparent
 * 401-refresh) and validates it at the boundary with Zod before any
 * consumer sees it.
 *
 * The backend endpoint is specified in beyonddemise/app-backend#140;
 * until it ships, the wire contract below is the frontend's proposal
 * (existing #163 schemas, wrapped in one envelope):
 *
 *   GET /api/account →
 *   {
 *     "account":  { "level": "Pro", "nextBilling": "1 Jan 2027",
 *                   "waitingAcceptance": 2, "pendingAcceptance": 3 },
 *     "reminder": { "date": "2025-12-24", "type": "email" },
 *     "waitingAcceptance": ["Peter Pan"],
 *     "pendingAcceptance": ["John Doe"]
 *   }
 *
 * One request serves all four dashboard consumers (`TdDashboard`,
 * `TdAccount`, `TdReminder`, 2× `TdAcceptance`): `loadDashboardData`
 * is single-flight and caches the resolved payload. Failed loads do
 * NOT stick — the next call retries, so a transient error on connect
 * doesn't freeze the dashboard until reload.
 */

import { z } from 'zod';
import { tdFetch } from './tdfetch.js';

/** Backend route serving the combined dashboard payload (app-backend#140). */
export const DASHBOARD_ENDPOINT = '/api/account';

// -----------------------------------------------------------------------------
// Boundary schemas (issue #163)
// -----------------------------------------------------------------------------

export const AccountDataSchema = z
  .object({
    level: z.string().min(1),
    nextBilling: z.string().min(1), // display string for now; TBD ISO-date when backend ships
    waitingAcceptance: z.number().int().nonnegative(),
    pendingAcceptance: z.number().int().nonnegative(),
  })
  .strict();
export type AccountData = z.infer<typeof AccountDataSchema>;

export const ReminderDataSchema = z
  .object({
    date: z.string().min(1),
    type: z.string().min(1),
  })
  .strict();
export type ReminderData = z.infer<typeof ReminderDataSchema>;

export const AcceptanceListSchema = z.array(z.string());
export type AcceptanceList = z.infer<typeof AcceptanceListSchema>;

/**
 * Combined `GET /api/account` response envelope (#239). Strict: an
 * unknown top-level field means the backend contract drifted and must
 * be renegotiated, not silently ignored.
 */
export const DashboardDataSchema = z
  .object({
    account: AccountDataSchema,
    reminder: ReminderDataSchema,
    waitingAcceptance: AcceptanceListSchema,
    pendingAcceptance: AcceptanceListSchema,
  })
  .strict();
export type DashboardData = z.infer<typeof DashboardDataSchema>;

// -----------------------------------------------------------------------------
// Typed errors
// -----------------------------------------------------------------------------

/**
 * Thrown by the dashboard fetchers when the backend response fails the
 * `DashboardDataSchema` boundary parse. The `reason` discriminator
 * mirrors the `AuthResponseError` pattern in `auth.ts`.
 */
export class DashboardResponseError extends Error {
  readonly reason: 'malformed-dashboard-data';
  readonly issues: z.core.$ZodIssue[];

  constructor(reason: DashboardResponseError['reason'], zodError: z.ZodError) {
    super(`${reason}: ${zodError.message}`);
    this.name = 'DashboardResponseError';
    this.reason = reason;
    this.issues = zodError.issues;
  }
}

/**
 * Thrown by the dashboard fetchers when `GET /api/account` answers with
 * a non-2xx status (after `tdFetch`'s transparent 401-refresh retry).
 * Carries the HTTP status so UI can distinguish "endpoint missing"
 * (404, backend#140 not shipped yet) from a server fault (5xx).
 */
export class DashboardHttpError extends Error {
  readonly status: number;

  constructor(status: number, statusText: string) {
    super(`dashboard endpoint returned ${status} ${statusText}`);
    this.name = 'DashboardHttpError';
    this.status = status;
  }
}

// -----------------------------------------------------------------------------
// Single-flight loader + per-consumer fetchers
// -----------------------------------------------------------------------------

const fetchDashboardData = async (): Promise<DashboardData> => {
  const response = await tdFetch(DASHBOARD_ENDPOINT);
  if (!response.ok) {
    throw new DashboardHttpError(response.status, response.statusText);
  }
  const raw: unknown = await response.json();
  const result = DashboardDataSchema.safeParse(raw);
  if (!result.success) {
    throw new DashboardResponseError('malformed-dashboard-data', result.error);
  }
  return result.data;
};

let inFlight: Promise<DashboardData> | null = null;

/**
 * Single-flight, cached dashboard load. The first caller triggers the
 * network request; concurrent and later callers share the same
 * promise, so the five components mounting on dashboard entry produce
 * exactly one `GET /api/account`. A rejected load clears the slot so
 * the next call retries instead of replaying the cached failure.
 *
 * @throws DashboardHttpError on non-2xx responses
 * @throws DashboardResponseError when the payload fails the schema
 */
export const loadDashboardData = (): Promise<DashboardData> => {
  if (inFlight === null) {
    inFlight = fetchDashboardData().catch((error: unknown) => {
      inFlight = null;
      throw error;
    });
  }
  return inFlight;
};

/**
 * Drop the cached payload / in-flight promise so the next
 * `loadDashboardData` call hits the network again. Test seam; also the
 * hook a future "refresh dashboard" affordance would call.
 *
 * @internal
 */
export const resetDashboardData = (): void => {
  inFlight = null;
};

/**
 * Account summary (tier level, billing, acceptance counters) from the
 * shared dashboard payload. Errors propagate — callers own their error
 * rendering (see `TdDashboard._loadAccountData`).
 */
export const fetchAccountData = async (): Promise<AccountData> => {
  const data = await loadDashboardData();
  return data.account;
};

/**
 * Next-reminder info from the shared dashboard payload. Errors
 * propagate to the caller (`TdReminder` catches and logs).
 */
export const fetchReminderData = async (): Promise<ReminderData> => {
  const data = await loadDashboardData();
  return data.reminder;
};

/**
 * Acceptance name list from the shared dashboard payload.
 *
 * @param type `'waiting'` or `'pending'`; any other value resolves to
 *   an empty list without touching the network (pre-#239 behaviour,
 *   pinned by tests).
 */
export const fetchAcceptanceData = async (
  type: string
): Promise<AcceptanceList> => {
  if (type !== 'waiting' && type !== 'pending') {
    return [];
  }
  const data = await loadDashboardData();
  return type === 'waiting' ? data.waitingAcceptance : data.pendingAcceptance;
};
